import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';

import { Alert } from '@components/Alert';
import { ModalItemType } from '@components/Modals/Modal';
import { NativeConfirmModal } from '@components/Modals/NativeConfirmModal';
import { TeamPaywallModal } from '@components/Modals/TeamPaywallModal';
import { MenuItem } from '@components/shared/Popup/PopupItem';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import {
  Project,
  ProjectMemberRole,
  Team,
  TeamMemberRole,
  Task,
  TaskMemberRole,
  useDeleteTaskMutation,
  useLeaveTaskMutation,
  useGetTaskQuery,
} from '@graphql/generated';
import useMe from '@hooks/useMe';
import useSelectedMembers from '@hooks/useSelectedMembers';
import { useSingleFlight } from '@hooks/useSingleFlight';
import useTask from '@hooks/useTask';
import { useAppNavigation } from '@navigation/useAppNavigation';
import { shouldShowPaywallForProject } from '@utils/teams/paywall';

export const useTaskDetailMenuActions = (
  task?: Pick<
    Task,
    | 'id'
    | 'userIds'
    | 'currentUserProjectRole'
    | 'currentUserProjectTeamRole'
    | 'currentUserRole'
    | 'creator'
    | 'projectId'
  >
) => {
  const { me } = useMe();
  const navigation = useAppNavigation();
  const { setCurrentTask } = useTask();
  const { setSelectedMembers } = useSelectedMembers();
  const { setShowDeleteTaskModal } = useTask();
  const { setPaywallModalOpen, setPaywallTeam } = useWebDrawer();

  // On mobile there is no WebDrawerProvider (paywall state is no-op), so we use local state and render the modal via paywallModalNode
  const [showPaywallMobile, setShowPaywallMobile] = useState(false);
  const [paywallTeamMobile, setPaywallTeamMobile] = useState<Team | undefined>(
    undefined
  );
  const [paywallProjectMobile, setPaywallProjectMobile] = useState<
    Project | undefined
  >(undefined);

  // Get full task to check paywall for duplicate
  const { data: taskData } = useGetTaskQuery({
    variables: { id: task?.id ?? '' },
    skip: !task?.id,
    fetchPolicy: 'cache-first',
  });
  const fullTask = taskData?.getTask;

  const {
    id: taskId,
    userIds = [],
    currentUserProjectRole,
    currentUserProjectTeamRole,
    currentUserRole,
    creator,
  } = task || { userIds: [] };
  const filteredUserIds = userIds?.filter((id) => id !== me?.id);

  const [leaveTask] = useLeaveTaskMutation({
    refetchQueries: ['listTasks', 'listProjects'],
  });

  const leaveThisTask = () => {
    taskId &&
      leaveTask({
        variables: {
          id: taskId,
          userId: me?.id || '',
        },
      });
  };
  const { t } = useTranslation();
  const creatorLeaveTaskAlert = () => {
    Alert.alert(
      t('models:teams.leave.title'),
      t('models:tasks.creatorLeaveMessage'),
      [
        { text: t('shared:cancel') },
        {
          text: t('shared:assign'),

          onPress: () => navigation.navigate('assign-new-creator', { taskId }),
        },
      ]
    );
  };
  const assigneeLeaveTaskAlert = () => {
    Alert.alert(
      t('models:teams.leave.titleTask'),
      t('models:tasks.assigneeLeaveMessage'),
      [
        { text: t('shared:cancel') },
        {
          text: t('shared:LeaveTask'),

          onPress: () => leaveThisTask(),
        },
      ]
    );
  };
  const [deleteTask] = useDeleteTaskMutation({
    // TA-10389 QA follow-up: `{ query: ListTasksDocument, variables: { id:
    // projectId } }` never matched any real query — `ListTasksQueryVariables`
    // has no `id` field, so it silently no-opped. `'listTasks'` alone only
    // refreshes Project Tasks' Hero carousel; the grouped tabs/counts the user
    // actually sees come from `listMyTasksProjectGroups` (TasksListView) and
    // `listTasksLight` (Unscheduled card), and Project Details' own task-count
    // badge comes from `getProject` — all four need a refetch so a deleted
    // task doesn't linger (or a stale count show) once the user backs out.
    refetchQueries: [
      'listMyTasksProjectGroups',
      'listTasksLight',
      'listTasks',
      'getProject',
    ],
  });

  // TA-10389: native has no in-app equivalent of the OS `Alert.alert` dialog
  // that can host a spinner or lock its buttons, so the destructive delete
  // confirm moves to the shared `NativeConfirmModal` (TA-10385) — the web
  // path (`setShowDeleteTaskModal(true)`) is untouched. `deleteTask` here is
  // never called from web (`task-detail-tabs.web.tsx` owns its own
  // `useDeleteTaskMutation` instance), so this state and its navigation are
  // native-only in practice even though the hook is shared.
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTaskLoading, setDeleteTaskLoading] = useState(false);
  const runDeleteOnce = useSingleFlight();

  const confirmDelete = () =>
    runDeleteOnce(async () => {
      setDeleteTaskLoading(true);
      try {
        // Mutations use a global `errorPolicy: 'all'`, so a GraphQL failure
        // resolves with top-level `errors` instead of rejecting — both paths
        // have to be checked (ProjectDetailModal.tsx has the same guard).
        const result = await deleteTask({ variables: { id: taskId } });
        if (result?.errors?.length) throw new Error('mutation returned errors');
        setShowDeleteConfirm(false);
        // Pops this popup menu, then the Task Detail screen behind it — once
        // deleted, that screen has nothing valid left to show.
        //
        // `StackActions.dispatch(StackActions.pop(2))` looks like the right
        // tool here but is actually a no-op past the first pop: this popup
        // (`task-details-modal`) is route index 1 of the NESTED `tasks-stack`
        // navigator, with `task-detail` sitting at index 0 — StackRouter's
        // POP handler clamps its count at the navigator's own first route
        // (`Math.max(index - count + 1, 1)`), so `pop(2)` silently behaves
        // exactly like `pop(1)`. Worse, because it still returns a non-null
        // state, React Navigation marks the action "handled" and it never
        // bubbles up to the parent `app-stack` navigator — where the Tasks
        // list actually lives — so the user was left stranded on Task
        // Details (TA-10389 QA, confirmed via a StackRouter reducer test).
        //
        // Two separate `goBack()` calls avoid this entirely: the first pops
        // this popup inside `tasks-stack` same as before; the second is
        // unhandled *there* (index 0 has nothing left to pop, StackRouter
        // returns null) and correctly bubbles to `app-stack`, popping the
        // whole `tasks-stack` route back to the Tasks list. Navigation state
        // is written synchronously to a ref, so the second call already sees
        // the post-first-pop state — no tick/await needed between them.
        navigation.goBack();
        navigation.goBack();
      } catch {
        // Leave the modal open on failure — the global spinner rules call
        // for a retry surface, not a silent close, on a failed destructive
        // action.
      } finally {
        setDeleteTaskLoading(false);
      }
    });

  const deleteAlert = () => {
    if (Platform.OS === 'web') {
      setShowDeleteTaskModal(true);
      return;
    }
    // No `goBack()` first — the confirmation now lives in this same popup
    // menu screen, and popping it here would unmount `NativeConfirmModal`
    // before the user ever saw it (TA-10386 lesson).
    setShowDeleteConfirm(true);
  };

  const deleteConfirmNode =
    Platform.OS !== 'web'
      ? React.createElement(NativeConfirmModal, {
          visible: showDeleteConfirm,
          loading: deleteTaskLoading,
          destructive: true,
          title: t('models:deleteTask.title'),
          message: t('models:deleteTask.message'),
          onCancel: () => setShowDeleteConfirm(false),
          onConfirm: confirmDelete,
        })
      : null;

  // TA-10619: "Copy Link to Invite", "Start a Chat", "Leave" and "Forward"
  // have no web implementation yet — on web they rendered disabled with a
  // "Coming Soon" tag, which reads as broken rather than intentional. Hide all
  // four from the web menu; web already exposes copy-link through the header's
  // own link icon (`TaskShareLink.web`), so nothing is lost there. Native is
  // untouched: there all four run real actions (`task-invite-link-modal` /
  // `task-members-select` / the leave alerts / `forward-message`), so they
  // stay visible.
  //
  // Hiding "Start a Chat" on web left `onPressAnnouncement` — and the chat
  // pagination query, its `fetchMore` cursor effects and `useCreateChatMutation`
  // that fed it — with no reachable caller on any platform, so they are gone
  // too. That plumbing ran on every Task Detail mount, paging the chat list
  // over the network for a menu item nobody could reach. When "Start a Chat"
  // ships on web, its handler comes back with the feature.
  //
  // This can empty the web menu entirely (a task Viewer has no other
  // permitted item), which is why the web task panel now hides its trigger
  // when nothing actionable is left — see `task-detail-tabs.web.tsx`.
  const isWeb = Platform.OS === 'web';

  const fullNavItems: (
    | (ModalItemType & {
        allowedRoles?: (TaskMemberRole | ProjectMemberRole | TeamMemberRole)[];
      })
    | (MenuItem & {
        allowedRoles?: (TaskMemberRole | ProjectMemberRole | TeamMemberRole)[];
      })
  )[] = [
    ...(!isWeb && filteredUserIds?.length
      ? ([
          {
            iconName: 'Link',
            title: t('shared:copyLinkToInvite'),
            name: t('shared:copyLinkToInvite'),
            onSelect: () => {
              navigation.goBack();
              navigation.navigate('task-invite-link-modal', { taskId });
            },
            allowedRoles: [
              TaskMemberRole.Assignee,
              TaskMemberRole.Owner,
              TaskMemberRole.Viewer,
              ProjectMemberRole.Owner,
              ProjectMemberRole.Admin,
              ProjectMemberRole.Editor,
              ProjectMemberRole.Viewer,
              TeamMemberRole.Admin,
              TeamMemberRole.Owner,
              TeamMemberRole.Member,
            ],
          },
          {
            iconName: 'MessageSquare',
            title: t('models:chat.startAChat'),
            name: t('models:chat.startAChat'),
            onSelect: () => {
              navigation.goBack();
              navigation.navigate('task-members-select', { taskId });
            },
            allowedRoles: [
              TaskMemberRole.Assignee,
              TaskMemberRole.Owner,
              ProjectMemberRole.Owner,
              ProjectMemberRole.Admin,
              ProjectMemberRole.Editor,
              TeamMemberRole.Admin,
              TeamMemberRole.Owner,
              TeamMemberRole.Member,
            ],
          },
        ] as (
          | (ModalItemType & {
              allowedRoles?: (
                | TaskMemberRole
                | ProjectMemberRole
                | TeamMemberRole
              )[];
            })
          | (MenuItem & {
              allowedRoles?: (
                | TaskMemberRole
                | ProjectMemberRole
                | TeamMemberRole
              )[];
            })
        )[])
      : []),
    {
      iconName: 'Duplicate',
      title: 'Duplicate',
      name: 'Duplicate',
      onSelect: () => {
        // Check paywall before navigating to duplicate
        if (
          fullTask?.project &&
          shouldShowPaywallForProject(fullTask.project)
        ) {
          if (Platform.OS === 'web') {
            navigation.goBack();
            setPaywallTeam(fullTask.project?.team ?? undefined);
            setPaywallModalOpen(true);
          } else {
            setPaywallTeamMobile(
              (fullTask.project?.team ?? undefined) as Team | undefined
            );
            setPaywallProjectMobile(
              (fullTask.project ?? undefined) as Project | undefined
            );
            setShowPaywallMobile(true);
          }
          return;
        }
        navigation.goBack();
        setCurrentTask(undefined);
        setSelectedMembers([]);
        navigation.navigate('task-copy-modal', { taskId });
      },
      allowedRoles: [
        TaskMemberRole.Owner,
        TaskMemberRole.Assignee,
        ProjectMemberRole.Owner,
        ProjectMemberRole.Admin,
        ProjectMemberRole.Editor,
        TeamMemberRole.Admin,
        TeamMemberRole.Owner,
      ],
    },
    ...(!isWeb
      ? [
          {
            iconName: 'LogOut' as const,
            title: 'Leave',
            name: 'Leave',
            onSelect: () => {
              navigation.goBack();
              if (creator?.id === me?.id) {
                creatorLeaveTaskAlert();
              } else {
                assigneeLeaveTaskAlert();
              }
            },
            allowedRoles: [
              TaskMemberRole.Owner,
              TaskMemberRole.Assignee,
              TeamMemberRole.Admin,
              TaskMemberRole.Viewer,
            ],
          },
          {
            iconName: 'Forward' as const,
            title: 'Forward',
            name: 'Forward',
            allowedRoles: [
              TaskMemberRole.Owner,
              TaskMemberRole.Assignee,
              ProjectMemberRole.Owner,
              ProjectMemberRole.Admin,
              ProjectMemberRole.Editor,
              // ProjectMemberRole.Viewer,
              TeamMemberRole.Admin,
              TeamMemberRole.Owner,
            ],
            onSelect: () => {
              navigation.goBack();
              navigation.navigate('forward-message', { taskId });
            },
          },
        ]
      : []),

    {
      iconName: 'Trash2',
      title: 'Delete',
      name: 'Delete',
      color: 'alertRed',
      isRed: true,
      onSelect: () => {
        // Web is unchanged: its confirm modal isn't hosted by this popup
        // screen, so closing the popup first is safe there. Native's
        // NativeConfirmModal IS hosted here, so popping first would unmount
        // it before the user ever saw it — the native path pops only after
        // the mutation resolves, inside `confirmDelete`.
        if (Platform.OS === 'web') navigation.goBack();
        deleteAlert();
      },
      allowedRoles: [
        TaskMemberRole.Owner,
        ProjectMemberRole.Owner,
        ProjectMemberRole.Admin,
        TeamMemberRole.Admin,
        TeamMemberRole.Owner,
      ],
    },
  ];

  const navItems = fullNavItems.filter((i) => {
    if (i.title == 'Leave')
      return i.allowedRoles?.some((r) => r === currentUserRole);
    else {
      return i.allowedRoles?.some(
        (r) =>
          r === currentUserRole ||
          r === currentUserProjectRole ||
          r === currentUserProjectTeamRole
      );
    }
  });

  const paywallModalNode =
    Platform.OS !== 'web' && showPaywallMobile
      ? React.createElement(TeamPaywallModal, {
          visible: showPaywallMobile,
          onClose: () => {
            setShowPaywallMobile(false);
            setPaywallTeamMobile(undefined);
            setPaywallProjectMobile(undefined);
          },
          team: paywallTeamMobile,
          project: paywallProjectMobile,
        })
      : null;

  return { navItems, paywallModalNode, deleteConfirmNode };
};
